import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Image,
  Link as ChakraLink,
  Text,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  Link as ReactRouterLink,
  matchPath,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useSidebarContext } from '@/context/sidebar.context';

import LangSwitcher from '@/elements/LangSwitcher/index';
import { Logo } from '@/elements/Logo/index';

import { RoutesEnum } from '@/enum/routes.enum';

import { useAuth } from '@/hooks/useAuth';
import { useNavigations } from '@/hooks/useNavigations';

import { triggerGTAGEvent } from '@/utils/analyticTracks';
import { ENABLED_LANGUAGES } from '@/utils/constants';

export const Sidebar = () => {
  const { isSidebarOpen, onSidebarClose } = useSidebarContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const navigations = useNavigations();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, username, logout } = useAuth();

  const goToSignup = () => {
    triggerGTAGEvent('signup.step1');
    navigate(RoutesEnum.REGISTRATION);
  };

  const goToLogin = () => {
    navigate(RoutesEnum.LOGIN);
  };

  const goToHome = () => {
    onSidebarClose();
    navigate(RoutesEnum.HOME);
  };

  const checkIsPath = (path: string) => Boolean(matchPath(pathname, path));

  return (
    <Drawer isOpen={isSidebarOpen} onClose={onSidebarClose} placement='left'>
      <DrawerOverlay backdropFilter='blur(8px)' />
      <DrawerContent backgroundColor={theme.colors.sidebar.bg} maxW='250px'>
        <DrawerHeader pt='50px'>
          <Button h='76px' mx='auto' onClick={goToHome} p='0' variant='ghost'>
            <Logo h='100%' w='100%' />
          </Button>
        </DrawerHeader>

        <DrawerBody
          display='flex'
          flexDirection='column'
          gap='4px'
          mt='64px'
          p='0'
        >
          {isAuthenticated &&
            navigations.map(({ icon, label, link }, idx) => (
              <ChakraLink
                _hover={{ textDecoration: 'none' }}
                as={ReactRouterLink}
                key={idx}
                to={link}
              >
                <Flex
                  _hover={{ color: !checkIsPath(link) && 'navbar.selected' }}
                  align='center'
                  bg={checkIsPath(link) ? 'navbar.selected' : ''}
                  color='navbar.text'
                  gap='24px'
                  p='16px'
                  position='relative'
                  w='100%'
                >
                  <Image as={icon} />
                  <Text
                    fontSize='16px'
                    fontWeight='500'
                    lineHeight='1.4'
                    textTransform='capitalize'
                  >
                    {label}
                  </Text>
                </Flex>
              </ChakraLink>
            ))}
        </DrawerBody>

        <DrawerFooter alignItems='center' flexDirection='column' gap='24px'>
          <Flex align='center' flex={1} justify='flex-end' w='100%'>
            <VStack spacing={6} w='100%'>
              {ENABLED_LANGUAGES.length > 1 && (
                <Box ml={2}>
                  <LangSwitcher />
                </Box>
              )}

              {isAuthenticated ? (
                <>
                  <Text color='white' fontSize='md' fontWeight='bold'>
                    {username}
                  </Text>
                  <Button
                    flex={1}
                    h='40px'
                    onClick={logout}
                    px={4}
                    size='md'
                    variant='outline'
                    w='100%'
                  >
                    {t('logout') || 'LOG OUT'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    h='40px'
                    onClick={goToLogin}
                    px={4}
                    size='md'
                    variant='outline'
                    w='100%'
                  >
                    {t('login')}
                  </Button>
                  <Button
                    h='40px'
                    onClick={goToSignup}
                    px={4}
                    size='md'
                    variant='primary'
                    w='100%'
                  >
                    {t('signUp')}
                  </Button>
                </>
              )}
            </VStack>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
